import React, { useState, useEffect } from 'react';
import { usePermissions } from '../contexts/PermissionsContext';
import {
  Box,
  Button,
  FormControlLabel,
  Switch,
  Menu,
  MenuItem,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Typography,
  AvatarGroup,
  Avatar,
  Chip,
  ListItemIcon,
  ListItemText,
  useTheme,
  Paper,
  Stack,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import {
  Delete as DeleteIcon,
  Flag as FlagIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
  Label as LabelIcon,
  Dashboard as DashboardIcon,
  MoreVert as MoreVertIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import * as kanTaskService from '../services/kanTaskService';
import CreateTaskModal from './CreateTaskModal';
import EditTaskModal from './EditTaskModal';
import { useAuth } from '../contexts/AuthContext';

// Styled components for clean Kanban UI
const StyledColumn = styled(Box)(({ theme, status }) => ({
  backgroundColor: theme.palette.mode === 'dark' 
    ? alpha(theme.palette.background.paper, 0.4) 
    : theme.palette.background.paper,
  borderRadius: 2,
  width: '300px',
  margin: theme.spacing(1),
  padding: 0,
  minHeight: '80vh',
  maxHeight: '80vh',
  overflowY: 'auto',
  border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
  position: 'relative',
  boxShadow: theme.palette.mode === 'dark'
    ? '0 2px 8px rgba(0,0,0,0.15)'
    : '0 1px 3px rgba(0,0,0,0.04)',
  '&::-webkit-scrollbar': {
    width: '4px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: alpha(theme.palette.divider, 0.3),
    borderRadius: '2px',
    '&:hover': {
      background: alpha(theme.palette.divider, 0.5),
    },
  },
}))

const StyledCard = styled(Card)(({ theme, isdragging }) => ({
  marginBottom: theme.spacing(1),
  cursor: 'pointer',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  backgroundColor: theme.palette.mode === 'dark' 
    ? alpha('#1a1a1a', 0.9) 
    : theme.palette.background.paper,
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? alpha(theme.palette.primary.main, 0.15) 
      : alpha(theme.palette.primary.main, 0.04),
  },
  ...(isdragging === 'true' && {
    opacity: 0.8,
    boxShadow: theme.palette.mode === 'dark'
      ? `0 16px 48px ${alpha(theme.palette.common.black, 0.8)}`
      : theme.shadows[8],
  }),
}));

const TaskCard = styled(Card)(({ theme, priority }) => ({
  marginBottom: theme.spacing(1),
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  backgroundColor: theme.palette.background.paper,
  borderRadius: 2,
  position: 'relative',
  overflow: 'visible',
  border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 2px 8px rgba(0,0,0,0.2)'
    : '0 1px 4px rgba(0,0,0,0.08)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: 3,
    height: '100%',
    background: priority === 'high' 
      ? theme.palette.error.main 
      : priority === 'normal' 
        ? theme.palette.primary.main 
        : alpha(theme.palette.divider, 0.3),
    borderTopLeftRadius: 2,
    borderBottomLeftRadius: 2,
  },
  '&:hover': {
    borderColor: alpha(theme.palette.primary.main, 0.2),
    boxShadow: theme.palette.mode === 'dark'
      ? '0 4px 16px rgba(0,0,0,0.3)'
      : '0 4px 12px rgba(0,0,0,0.12)',
  },
}));

const TaskImage = styled('img')({
  width: '100%',
  height: 140,
  objectFit: 'cover',
  borderRadius: '4px 4px 0 0',
});

const ColumnHeader = styled(Box)(({ theme, status }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(1.25, 1.5),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
  backgroundColor: 'transparent',
}));

const KanbanBoard = ({
  projectId,
  tasks = {
    pending: [],
    'in-progress': [],
    review: [],
    completed: []
  },
  onTasksUpdate = () => {},
  teamMembers = []
}) => {
  const { user } = useAuth();
  const theme = useTheme();
  const { can } = usePermissions();
  const canView = can('tasks.view');
  const canManage = can('tasks.manage');
  const [draggedTask, setDraggedTask] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isTaskFormOpen, setIsTaskFormOpen] = useState(false);
  const [isEditTaskOpen, setIsEditTaskOpen] = useState(false);
  const [showOnlyMyTasks, setShowOnlyMyTasks] = useState(false);
  // Filter tasks to only show those created by or assigned to the current user
  // Using useMemo to avoid recalculating on every render
  const filteredTasks = React.useMemo(() => {
    // If no user is logged in, return all tasks
    if (!user) return tasks;

    // Extract user ID from the user object
    const currentUserId = user._id || (user.user && user.user._id) || user.id;
    
    // If we don't have a user ID, we can't filter properly
    if (!currentUserId) {
      console.warn('No user ID available for filtering, showing all tasks');
      return tasks;
    }
    
    console.log('Filtering tasks for user:', user.name || user.email, 'with ID:', currentUserId);
    
    // Filter tasks to only show those created by or assigned to the current user
    const filtered = {};
    Object.keys(tasks).forEach(status => {
      filtered[status] = tasks[status].filter(task => {
        // Skip tasks without title or ID for safety
        if (!task || !task._id) return false;
        
        // Check if the user created this task (is in watchers array)
        if (task.watchers && Array.isArray(task.watchers) && task.watchers.length > 0) {
          const isCreator = task.watchers.some(watcher => {
            // Extract watcher ID based on format
            const watcherId = typeof watcher === 'string' ? watcher : 
                            (watcher && watcher._id) ? watcher._id : null;
            
            // Check if the current user is a watcher
            return watcherId === currentUserId;
          });
          
          if (isCreator) {
            console.log(`User ${currentUserId} is a watcher of task: ${task.title}`);
            return true;
          }
        }
        
        // Check if the user is assigned as a crew member
        if (task.crewMembers && Array.isArray(task.crewMembers) && task.crewMembers.length > 0) {
          return task.crewMembers.some(member => {
            if (!member) return false;
            
            // Extract member ID based on format
            let memberId = null;
            let userId = null;
            
            if (typeof member === 'string') {
              // This could be either a User ID or a CrewMember ID
              memberId = member;
            } else if (member && member._id) {
              // This could be a User object or a CrewMember object
              memberId = String(member._id);
              
              // If it's a CrewMember object with a user property
              if (member.user && member.user._id) {
                userId = String(member.user._id);
              }
            } else if (member && member.user && member.user._id) {
              // This is definitely a CrewMember object
              memberId = String(member._id);
              userId = String(member.user._id);
            }
            
            // Check if the user is directly assigned as a crew member
            if (memberId === currentUserId) {
              console.log(`User ${currentUserId} is directly assigned as crew member for task: ${task.title}`);
              return true;
            }
            
            // Check if the user is assigned via their user ID in a crew member object
            if (userId === currentUserId) {
              console.log(`User ${currentUserId} is assigned via user ID in crew member for task: ${task.title}`);
              return true;
            }
            
            // Check if the user is part of the team members for this task
            const isTeamMember = teamMembers.some(teamMember => {
              // Check if this team member belongs to the current user
              if (teamMember.user && teamMember.user._id === currentUserId) {
                // Now check if this team member's ID matches the crew member ID
                return teamMember._id === memberId;
              }
              return false;
            });
            
            if (isTeamMember) {
              console.log(`User ${currentUserId} is part of team members for task: ${task.title}`);
              return true;
            }
            
            return false;
          });
        }
        
        // No match found - this task is not created by or assigned to the current user
        return false;
      });
    });
    
    return filtered;
  }, [tasks, user]); // Only recalculate when tasks or user changes
  
  // Helper: derive current user id consistently
  const currentUserId = React.useMemo(() => {
    if (!user) return null;
    return user._id || (user.user && user.user._id) || user.id || null;
  }, [user]);

  // Authorization: managers can edit any task, or creator/assignee can edit their own tasks
  const canEditTask = React.useCallback((task) => {
    if (!task) return false;
    // Users with manage permission can edit any task
    if (canManage) return true;
    // Otherwise, check if user is creator or assignee
    if (!currentUserId) return false;
    
    // Check if user is the creator via createdBy field
    const createdById = task.createdBy 
      ? (typeof task.createdBy === 'string' ? task.createdBy : task.createdBy._id)
      : null;
    const isCreator = createdById && String(createdById) === String(currentUserId);
    
    // Also check watchers array (can be strings or objects)
    const isWatcher = Array.isArray(task.watchers) && task.watchers.some(w => {
      const wid = typeof w === 'string' ? w : (w && w._id) ? String(w._id) : null;
      return wid === String(currentUserId);
    });
    
    // crewMembers can be string IDs or user objects
    const isAssignee = Array.isArray(task.crewMembers) && task.crewMembers.some(m => {
      if (!m) return false;
      let id = null;
      if (typeof m === 'string') id = m;
      else if (m && m._id) id = String(m._id);
      else if (m && m.user && m.user._id) id = String(m.user._id);
      return id === String(currentUserId);
    });
    return !!(isCreator || isWatcher || isAssignee);
  }, [currentUserId, canManage]);
  
  // Helper function to get tasks depending on toggle
  const getFilteredTasks = () => (showOnlyMyTasks ? filteredTasks : tasks);

  // No toggle function needed anymore as we always filter tasks

  const handleDragStart = (e, task) => {
    // Block drag if user cannot edit this task
    if (!canEditTask(task)) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.setData('taskId', task._id);
    e.dataTransfer.setData('currentStatus', task.status);
    setDraggedTask(task._id);
  };

  const handleDragEnd = (e) => {
    setDraggedTask(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    // Add visual feedback for the drop target
    const column = e.currentTarget;
    // Use a consistent grey highlight regardless of column
    column.style.backgroundColor = theme.palette.mode === 'dark'
      ? '#1e2124' // consistent dark grey
      : '#ebecf0'; // consistent light grey
  };

  const handleDragLeave = (e) => {
    // Remove visual feedback
    const column = e.currentTarget;
    // Revert to default styling (allow StyledColumn styles to apply)
    column.style.backgroundColor = '';
  };

  const handleMenuClick = (event, task) => {
    event.stopPropagation();
    // Disallow opening menu if user cannot edit this task
    if (!canEditTask(task)) return;
    setSelectedTask(task);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = async (action) => {
    handleMenuClose();
    
    if (!selectedTask) {
      console.error('No task selected for action:', action);
      return;
    }

    try {
      switch (action) {
        case 'delete':
          await handleDeleteTask(selectedTask);
          break;
        default:
          console.warn('Unknown menu action:', action);
          break;
      }
    } catch (error) {
      console.error('Error handling menu action:', error);
    }
    
    setSelectedTask(null);
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData('taskId');
    const currentStatus = e.dataTransfer.getData('currentStatus');

    if (currentStatus === newStatus) return;

    const task = tasks[currentStatus].find(t => t._id === taskId);
    if (!task) return;

    const previousTasks = tasks;
    const optimisticTask = { ...task, status: newStatus };
    const optimisticTasks = {
      ...tasks,
      [currentStatus]: tasks[currentStatus].filter(t => t._id !== taskId),
      [newStatus]: [...tasks[newStatus], optimisticTask]
    };

    onTasksUpdate(optimisticTasks);

    try {
      console.log('Moving task between columns:', task.title);
      console.log('Original task data:', JSON.stringify(task));
      
      // Make a deep copy of the task to preserve all properties
      const taskToUpdate = {
        ...JSON.parse(JSON.stringify(task)),
        status: newStatus
      };
      
      // Send the update to the server
      const updatedTask = await kanTaskService.updateTask(taskId, taskToUpdate);
      console.log('Server returned updated task:', JSON.stringify(updatedTask));
      
      // If the server doesn't return the full task data, merge with original
      const mergedTask = {
        ...task,
        ...updatedTask,
        status: newStatus
      };
      
      // Ensure crew members are preserved
      if (!mergedTask.crewMembers && task.crewMembers) {
        mergedTask.crewMembers = task.crewMembers;
      }
      
      console.log('Final task data after merge:', JSON.stringify(mergedTask));

      const syncedTasks = {
        ...optimisticTasks,
        [newStatus]: optimisticTasks[newStatus].map(t =>
          t._id === taskId ? mergedTask : t
        )
      };

      onTasksUpdate(syncedTasks);
    } catch (error) {
      console.error('Error updating task status:', error);
      onTasksUpdate(previousTasks);
    }
  };

  const handleAddTask = async (taskData) => {
    try {
      if (!taskData.title?.trim()) {
        throw new Error('Title is required');
      }

      // Ensure projectId is set
      if (!projectId) {
        throw new Error('Project ID is required');
      }

      // Filter out messages with invalid sender IDs and format remaining messages
      const validMessages = (taskData.messages || [])
        .filter(msg => msg && msg.content && msg.sender && msg.sender.match(/^[0-9a-fA-F]{24}$/))
        .map(msg => ({
          content: msg.content.trim(),
          sender: msg.sender,
          senderName: msg.senderName || 'Anonymous',
          timestamp: msg.timestamp || new Date().toISOString()
        }));

      // Process crew members to ensure we have valid IDs
      console.log('Raw crew members data for add task:', JSON.stringify(taskData.crewMembers));
      
      // Ensure crewMembers is an array
      const crewMembersArray = Array.isArray(taskData.crewMembers) ? taskData.crewMembers : [];
      console.log('Crew members array for add task:', JSON.stringify(crewMembersArray));
      
      const processedCrewMembers = crewMembersArray.map(member => {
        console.log('Processing crew member for add task:', JSON.stringify(member));
        let memberId = null;
        
        if (typeof member === 'string') {
          memberId = member; // Already an ID
          console.log('Using string ID for add task:', memberId);
        } else if (member && member._id) {
          memberId = member._id; // Extract ID from object
          console.log('Extracted ID from object for add task:', memberId);
        } else if (member && member.user && member.user._id) {
          memberId = member.user._id; // Extract ID from nested user object
          console.log('Extracted ID from nested user object for add task:', memberId);
        } else {
          console.log('Could not extract ID from member for add task:', JSON.stringify(member));
        }
        return memberId;
      }).filter(id => {
        const isValid = Boolean(id);
        if (!isValid) {
          console.log('Filtered out invalid crew member ID for add task');
        }
        return isValid;
      }); // Remove any null values

      console.log('Final processed crew members for add task:', processedCrewMembers);

      console.log('Task image data received:', taskData.image ? `Image present (${typeof taskData.image}, length: ${taskData.image.length})` : 'No image');
      
      const newTaskData = {
        ...taskData,
        projectId,
        status: 'pending',
        assignees: [], // We're not using assignees anymore
        castMembers: [], // We're not using cast members anymore
        crewMembers: processedCrewMembers,
        image: taskData.image || null,
        messages: validMessages
      };

      // Remove any undefined or null values (except image which can be null)
      Object.keys(newTaskData).forEach(key => {
        if (key !== 'image' && (newTaskData[key] === undefined || newTaskData[key] === null)) {
          delete newTaskData[key];
        }
      });

      // Remove _id if it exists
      delete newTaskData._id;

      console.log('Creating new task with data:', newTaskData);
      const newTask = await kanTaskService.createTask(newTaskData);
      console.log('New task created:', newTask);
      console.log('New task image from server:', newTask.image ? `Image URL: ${newTask.image.substring(0, 100)}...` : 'No image');
      
      // Ensure crew members are properly handled in the response
      // If the server didn't populate crew members but we have them in the request, use those
      const taskWithCrewMembers = {
        ...newTask,
        crewMembers: newTask.crewMembers && newTask.crewMembers.length > 0 
          ? newTask.crewMembers 
          : processedCrewMembers.map(id => ({ _id: id }))
      };
      
      console.log('Using created task with crew members:', JSON.stringify(taskWithCrewMembers.crewMembers));

      // Update the local state with the new task
      const updatedTasks = {
        ...tasks,
        pending: [...(tasks.pending || []), taskWithCrewMembers]
      };

      console.log('Updating tasks state:', updatedTasks);
      onTasksUpdate(updatedTasks);
      setIsTaskFormOpen(false);
    } catch (error) {
      console.error('Error creating task:', error);
      // Here you might want to show an error message to the user
      throw error;
    }
  };

  const handleDeleteTask = async (task) => {
    try {
      if (!task?._id) {
        console.error('No task ID provided for deletion');
        return;
      }

      // Find the current status of the task
      const currentStatus = Object.keys(tasks).find(status =>
        tasks[status].some(t => t._id === task._id)
      );

      if (!currentStatus) {
        console.error('Could not find task status for deletion');
        return;
      }

      console.log('Deleting task:', task._id, 'from status:', currentStatus);
      await kanTaskService.deleteTask(task._id);

      // Update the local state by removing the task
      const newTasks = { ...tasks };
      newTasks[currentStatus] = newTasks[currentStatus].filter(t => t._id !== task._id);

      console.log('Updating tasks after deletion:', newTasks);
      onTasksUpdate(newTasks);
    } catch (error) {
      console.error('Error deleting task:', error);
      // Here you might want to show an error message to the user
    }
  };

  const handleEditTask = async (taskData) => {
    try {
      // Ensure we have the task ID
      if (!taskData._id) {
        console.error('No task ID provided for update');
        throw new Error('Missing task identifier');
      }

      // Find the current status of the task
      const status = Object.keys(tasks).find(status =>
        tasks[status].some(t => t._id === taskData._id)
      );

      if (!status) {
        console.error('Could not find task status');
        return;
      }

      // Process crew members to ensure we have valid IDs
      console.log('Raw crew members data:', JSON.stringify(taskData.crewMembers));
      
      // Ensure crewMembers is an array
      const crewMembersArray = Array.isArray(taskData.crewMembers) ? taskData.crewMembers : [];
      console.log('Crew members array:', JSON.stringify(crewMembersArray));
      
      const processedCrewMembers = crewMembersArray.map(member => {
        console.log('Processing crew member for edit:', JSON.stringify(member));
        let memberId = null;
        
        if (typeof member === 'string') {
          memberId = member; // Already an ID
          console.log('Using string ID:', memberId);
        } else if (member && member._id) {
          memberId = member._id; // Extract ID from object
          console.log('Extracted ID from object:', memberId);
        } else if (member && member.user && member.user._id) {
          memberId = member.user._id; // Extract ID from nested user object
          console.log('Extracted ID from nested user object:', memberId);
        } else {
          console.log('Could not extract ID from member:', JSON.stringify(member));
        }
        return memberId;
      }).filter(id => {
        const isValid = Boolean(id);
        if (!isValid) {
          console.log('Filtered out invalid crew member ID');
        }
        return isValid;
      }); // Remove any null values

      console.log('Final processed crew members for edit:', processedCrewMembers);

      // Format the task data for the API
      const updatedTaskData = {
        ...taskData,
        status,
        assignees: [], // We're not using assignees anymore
        castMembers: [], // We're not using cast members anymore
        crewMembers: processedCrewMembers,
      };

      // Don't send imageFilePath - it's managed by the server based on the image field
      delete updatedTaskData.imageFilePath;

      console.log('handleEditTask - image field:', JSON.stringify(updatedTaskData.image));
      console.log('handleEditTask - image type:', typeof updatedTaskData.image);
      console.log('handleEditTask - image is empty string:', updatedTaskData.image === '');

      // Call the API to update the task
      const response = await kanTaskService.updateTask(taskData._id, updatedTaskData);

      if (response) {
        console.log('Task update response from server:', JSON.stringify(response));
        
        // Ensure crew members are properly handled in the response
        // If the server didn't populate crew members but we have them in the request, use those
        // First, find the original task to preserve any existing crew member data
        const originalTask = tasks[status].find(t => t._id === taskData._id);
        console.log('Original task before update:', JSON.stringify(originalTask));
        
        // Determine the best crew members to use
        let bestCrewMembers = [];
        
        // Option 1: Use populated crew members from the response if available
        if (response.crewMembers && response.crewMembers.length > 0) {
          console.log('Using crew members from server response');
          bestCrewMembers = response.crewMembers;
        } 
        // Option 2: Use the processed crew members from the form submission
        else if (processedCrewMembers && processedCrewMembers.length > 0) {
          console.log('Using processed crew members from form submission');
          bestCrewMembers = processedCrewMembers.map(id => ({ _id: id }));
        }
        // Option 3: Preserve the original crew members if available
        else if (originalTask && originalTask.crewMembers && originalTask.crewMembers.length > 0) {
          console.log('Preserving original crew members');
          bestCrewMembers = originalTask.crewMembers;
        }
        
        const responseWithCrewMembers = {
          ...response,
          crewMembers: bestCrewMembers
        };
        
        console.log('Using response with crew members:', JSON.stringify(responseWithCrewMembers.crewMembers));
        
        // Update the local state
        let nextTasksState;
        if (projectId) {
          try {
            console.log('Refreshing tasks from server after edit to ensure persistence');
            nextTasksState = await kanTaskService.getProjectTasks(projectId);
          } catch (refreshError) {
            console.warn('Failed to refresh tasks after edit, falling back to local state', refreshError);
          }
        }

        if (!nextTasksState) {
          const newTasks = { ...tasks };
          newTasks[status] = newTasks[status].map(task =>
            task._id === taskData._id ? { ...task, ...responseWithCrewMembers } : task
          );
          nextTasksState = newTasks;
        }

        onTasksUpdate(nextTasksState);
        setIsEditTaskOpen(false);
        setSelectedTask(null);
        return responseWithCrewMembers;
      }
    } catch (error) {
      console.error('Error updating task:', error);
      throw error;
    }
  };

  const getPriorityColor = (priority) => {
    // Use theme-aware colors for priorities
    if (theme.palette.mode === 'dark') {
      switch (priority?.toLowerCase()) {
        case 'high':
          return '#ff6b6b'; // Brighter red for dark mode
        case 'normal':
          return '#69db7c'; // Brighter green for dark mode
        case 'low':
          return '#ffd43b'; // Brighter yellow for dark mode
        default:
          return '#69db7c';
      }
    } else {
      switch (priority?.toLowerCase()) {
        case 'high':
          return '#eb5a46';
        case 'normal':
          return '#61bd4f';
        case 'low':
          return '#f2d600';
        default:
          return '#61bd4f';
      }
    }
  };

  const renderTask = (task) => (
    <TaskCard
      key={task._id}
      draggable={canEditTask(task)}
      priority={task.priority}
      onDragStart={(e) => handleDragStart(e, task)}
      onDragEnd={handleDragEnd}
      onClick={() => {
        if (canEditTask(task)) {
          setSelectedTask(task);
          setIsEditTaskOpen(true);
        }
      }}
    >
      {task.image && (
        <Box sx={{ height: 140, overflow: 'hidden', position: 'relative' }}>
          <TaskImage 
            src={task.image} 
            alt={task.title}
            loading="lazy"
            onError={(e) => {
              console.error('Error loading image for task:', task.title, 'URL:', task.image, e);
              e.target.style.display = 'none';
              // Show a fallback element
              const fallbackEl = document.createElement('div');
              fallbackEl.style.height = '100%';
              fallbackEl.style.width = '100%';
              fallbackEl.style.backgroundColor = '#f0f0f0';
              fallbackEl.style.display = 'flex';
              fallbackEl.style.alignItems = 'center';
              fallbackEl.style.justifyContent = 'center';
              fallbackEl.innerHTML = '<svg width="24" height="24" viewBox="0 0 24 24"><path fill="#aaa" d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>';
              e.target.parentNode.appendChild(fallbackEl);
            }}
          />
          <Box 
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
              color: 'white',
              p: 1,
              fontSize: '0.75rem',
              fontWeight: 'bold',
              textShadow: '0 1px 2px rgba(0,0,0,0.8)'
            }}
          >
            {task.title}
          </Box>
        </Box>
      )}
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="subtitle1" sx={{ 
            fontWeight: 600, 
            fontSize: '0.9rem',
            color: 'text.primary',
            lineHeight: 1.3
          }}>
            {task.title}
          </Typography>
          {canEditTask(task) && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleMenuClick(e, task);
            }}
            sx={{ 
              ml: 1, 
              mt: -0.5,
              color: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.7) : alpha(theme.palette.common.black, 0.6),
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' 
                  ? alpha(theme.palette.primary.main, 0.15) 
                  : alpha(theme.palette.primary.main, 0.08),
              }
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          )}
        </Box>

        {task.description && (
          <Box sx={{ mt: 0.5, mb: 1.5 }}>
            <Typography variant="body2" sx={{ 
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              color: 'text.secondary',
              lineHeight: 1.5,
              fontSize: '0.8rem',
            }}>
              {task.description}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5, gap: 1, flexWrap: 'wrap' }}>
          {task.priority && (
            <Chip
              label={task.priority}
              size="small"
              variant="outlined"
              sx={{ 
                height: 18, 
                borderRadius: 1,
                fontWeight: 500,
                fontSize: '0.65rem',
                textTransform: 'capitalize',
                borderColor: task.priority === 'high' 
                  ? theme.palette.error.main 
                  : task.priority === 'normal' 
                    ? theme.palette.primary.main 
                    : theme.palette.divider,
                color: task.priority === 'high' 
                  ? theme.palette.error.main 
                  : task.priority === 'normal' 
                    ? theme.palette.primary.main 
                    : 'text.secondary',
                '& .MuiChip-label': { px: 0.75 }
              }}
            />
          )}
          {task.phase && (
            <Chip
              label={task.phase}
              size="small"
              variant="outlined"
              sx={{ 
                height: 18, 
                borderRadius: 1,
                fontWeight: 500,
                fontSize: '0.65rem',
                textTransform: 'capitalize',
                borderColor: alpha(theme.palette.primary.main, 0.3),
                color: 'text.secondary',
                '& .MuiChip-label': { px: 0.75 }
              }}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5 }}>
          <AvatarGroup 
            max={3} 
            sx={{ 
              '& .MuiAvatar-root': { 
                width: 24, 
                height: 24, 
                fontSize: '0.7rem',
                border: `2px solid ${theme.palette.background.paper}`,
              } 
            }}
          >
            {task.assignees?.map((assignee) => (
              <Avatar
                key={assignee._id}
                src={assignee.avatar}
                alt={assignee.name}
                sx={{ 
                  width: 24, 
                  height: 24
                }}
              />
            ))}
          </AvatarGroup>
        </Box>
      </CardContent>
    </TaskCard>
  );

  const columnTitles = {
    pending: 'To Do',
    'in-progress': 'In Progress',
    review: 'Review',
    completed: 'Done'
  };

  const renderTaskMenu = () => (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleMenuClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      <MenuItem 
        onClick={() => handleMenuItemClick('delete')}
        sx={{ color: 'error.main' }}
      >
        <ListItemIcon>
          <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
        </ListItemIcon>
        <ListItemText>Delete</ListItemText>
      </MenuItem>
    </Menu>
  );

  // Permission check - show access denied if no view permission
  if (!canView) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Access Restricted
        </Typography>
        <Typography color="text.secondary">
          You don't have permission to view tasks. Contact your project administrator for access.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      overflowX: 'auto', 
      minHeight: '90vh', 
      bgcolor: (theme) => theme.palette.mode === 'dark' ? '#000' : '#fff', 
      p: 3
    }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: (theme) => `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        py: 1,
        px: 2,
        mb: 2
      }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1.5,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <DashboardIcon sx={{ color: theme.palette.primary.main, fontSize: 16 }} />
          </Box>
          <Typography variant="subtitle1" sx={{
            fontWeight: 600,
            color: 'text.primary'
          }}>
            Tasks
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={showOnlyMyTasks}
                onChange={(e) => setShowOnlyMyTasks(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label={showOnlyMyTasks ? 'My tasks' : 'All'}
            sx={{ mr: 1, ml: 1, '& .MuiTypography-root': { fontSize: '0.75rem' } }}
          />
          <Tooltip title="Add task" arrow>
            <IconButton
              onClick={() => setIsTaskFormOpen(true)}
              disabled={!canManage}
              size="small"
              sx={{ color: theme.palette.primary.main }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, pb: 2 }}>
        {Object.keys(getFilteredTasks()).map((status) => (
          <StyledColumn
            key={status}
            status={status}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={(e) => {
              handleDrop(e, status);
              // Revert to default styling (allow StyledColumn styles to apply)
              e.currentTarget.style.backgroundColor = '';
            }}
          >
            <ColumnHeader status={status}>
              <Typography variant="h6" sx={{ 
                fontSize: '0.9rem', 
                fontWeight: 600,
                color: status === 'pending' ? '#1976d2' : 
                       status === 'in-progress' ? '#ed6c02' : 
                       status === 'review' ? '#5e35b1' : 
                       '#2e7d32',
              }}>
                {columnTitles[status]} ({getFilteredTasks()[status].length})
              </Typography>
              <IconButton size="small" sx={{ color: 'text.secondary' }}>
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </ColumnHeader>
            <Box sx={{ p: 1.5 }}>
              {getFilteredTasks()[status].map(renderTask)}
              {getFilteredTasks()[status].length === 0 && (
                <Box sx={{ 
                  py: 4, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  color: theme.palette.mode === 'dark' ? alpha(theme.palette.text.secondary, 0.7) : theme.palette.text.disabled,
                  borderRadius: 2,
                  border: '1px dashed',
                  borderColor: theme.palette.mode === 'dark' ? alpha(theme.palette.divider, 0.3) : theme.palette.divider,
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? alpha(theme.palette.background.paper, 0.2) 
                    : alpha(theme.palette.background.paper, 0.4),
                  backdropFilter: 'blur(8px)',
                  boxShadow: theme.palette.mode === 'dark'
                    ? 'inset 0 0 20px rgba(0,0,0,0.1)'
                    : 'inset 0 0 20px rgba(0,0,0,0.03)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: theme.palette.mode === 'dark' 
                      ? alpha(theme.palette.primary.main, 0.3) 
                      : alpha(theme.palette.primary.main, 0.2),
                    backgroundColor: theme.palette.mode === 'dark' 
                      ? alpha(theme.palette.background.paper, 0.3) 
                      : alpha(theme.palette.background.paper, 0.5),
                  }
                }}>
                  <Typography variant="body2" sx={{ 
                    mb: 1.5, 
                    fontWeight: 500,
                    textShadow: theme.palette.mode === 'dark' ? '0 0 10px rgba(0,0,0,0.3)' : 'none'
                  }}>
                    No tasks
                  </Typography>
                  <Typography variant="caption" sx={{
                    opacity: 0.8,
                    letterSpacing: '0.4px',
                    textShadow: theme.palette.mode === 'dark' ? '0 0 8px rgba(0,0,0,0.3)' : 'none'
                  }}>
                    Drag tasks here
                  </Typography>
                </Box>
              )}
            </Box>
          </StyledColumn>
        ))}
      </Box>

      {renderTaskMenu()}

      {/* Create Task Modal */}
      <CreateTaskModal
        open={isTaskFormOpen}
        onClose={() => setIsTaskFormOpen(false)}
        onSubmit={handleAddTask}
        teamMembers={teamMembers}
        castMembers={teamMembers}
      />

      {/* Edit Task Modal */}
      <EditTaskModal
        open={isEditTaskOpen}
        onClose={() => {
          setIsEditTaskOpen(false);
          setSelectedTask(null);
        }}
        onTaskUpdate={handleEditTask}
        task={selectedTask}
        teamMembers={teamMembers}
        castMembers={teamMembers}
      />
    </Box>
  );
};

export default KanbanBoard;
